// src/pages/StartOfDay.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Chip,
  Paper,
  Stack,
  Container,
  TextField,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getOptimalFloat, activateCashDrawer, upsertCashDrawerStoreStatus } from 'api';
import type { CashDrawer } from '../api/types';
import { useCurrencyInput } from 'hooks/useCurrencyInput';

interface LocationState {
  drawer?: CashDrawer;
}

const StartOfDay: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const drawer = (location.state as LocationState)?.drawer;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentFloat = drawer?.float ?? 0;
  const snackOffset = isMobile ? '120px' : '200px';

  const [loading, setLoading] = useState(true);
  const [optimumFloat, setOptimumFloat] = useState<number>(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const floatInput = useCurrencyInput(currentFloat);
  const floatValue = floatInput.numericValue;

  useEffect(() => {
    const fetchOptimal = async () => {
      setLoading(true);
      try {
        const optimal = await getOptimalFloat();
        setOptimumFloat(optimal);
      } catch (e) {
        console.error(e);
        setErrorMessage('Failed to fetch optimal float');
        setErrorToastOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchOptimal();
  }, [drawer]);

  const handleSubmit = async () => {
    const actualTotal = floatValue;
    if (actualTotal < 0) return;

    setLoading(true);
    try {
      await activateCashDrawer({
        float_amount: { value: actualTotal, currency: 'USD' },
      });
      await upsertCashDrawerStoreStatus({
        activationTimestamp: new Date().toISOString(),
        deactivationTimestamp: null,
      });
      setToastOpen(true);
    } catch (err: unknown) {
      console.error(err);
      let msg = 'An unexpected error occurred';
      if (axios.isAxiosError(err)) {
        msg = err.response?.data?.message ?? err.message;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setErrorMessage(msg);
      setErrorToastOpen(true);
      setLoading(false);
      return;
    }
    setLoading(false);
    setTimeout(() => navigate('/'), 2000);
  };

  const glassPanel = {
    p: 2,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    background: alpha(theme.palette.primary.light, 0.05),
    backdropFilter: 'saturate(120%) blur(4px)',
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Start of Day</Typography>}
        showBackButton
        onBack={() => navigate('/')}
        sticky
        align="left"
        elevateOnScroll
      />

      <Backdrop sx={{ color: '#fff', zIndex: theme.zIndex.modal + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container
        disableGutters
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 720, md: 880 },
          mx: 'auto',
        }}
      >
        <Box
          component="main"
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Container
            disableGutters
            sx={{
              maxWidth: { xs: '100%', sm: 720, md: 880 },
              mx: 'auto',
            }}
          >
            {!loading && (
              <>
                {/* Context card: Optimum & Current float */}
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  }}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" color="text.secondary">
                          Optimal Cash on Hand
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`$${optimumFloat.toFixed(2)}`}
                          sx={{ borderRadius: 2 }}
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" color="text.secondary">
                          Actual Cash on Hand
                        </Typography>
                        <Chip
                          size="small"
                          color="default"
                          variant="outlined"
                          label={`$${currentFloat.toFixed(2)}`}
                          sx={{ borderRadius: 2 }}
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                  <Divider />
                  <Box
                    sx={(t) => ({
                      px: 2,
                      py: 1,
                      background:
                        t.palette.mode === 'light'
                          ? alpha(t.palette.primary.main, 0.06)
                          : alpha(t.palette.primary.main, 0.18),
                    })}
                  >
                    <Typography variant="caption" color="text.secondary">
                      The actual cash on hand shown is from the previous close.
                    </Typography>
                  </Box>
                </Card>

                {/* Starting float panel */}
                <Paper elevation={0} sx={{ ...glassPanel, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Enter Starting Float
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                    Use the current float amount as a starting point and adjust if the drawer begins with a different total.
                  </Typography>

                  <TextField
                    fullWidth
                    type="text"
                    placeholder="Enter amount"
                    inputRef={floatInput.inputRef}
                    value={floatInput.formattedValue}
                    onKeyDown={floatInput.onKeyDown}
                    onFocus={floatInput.onFocus}
                    disabled={true}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                        transition: 'box-shadow .2s ease',
                        '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                      },
                      '& .MuiInputBase-input': {
                        textAlign: 'center',
                        fontSize: { xs: 20, sm: 22 },
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        caretColor: 'transparent',
                      },
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      enterKeyHint: 'done',
                    }}
                  />

                  {/* <Divider sx={{ my: 2 }} /> */}

                  {/* <Card
                    variant="outlined"
                    sx={{ borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.9) }}
                  >
                    <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        color="primary"
                        label={`Total: $${floatValue.toFixed(2)}`}
                        sx={{
                          borderRadius: 2,
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontWeight: 700,
                          px: 3,
                          py: 1,
                        }}
                      />
                    </CardContent>
                  </Card> */}
                </Paper>
              </>
            )}
          </Container>
        </Box>
      </Container>
      <Footer onSubmit={handleSubmit} disabled={loading || floatValue < 0} elevated />

      {/* Success Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => {
          setToastOpen(false);
          navigate('/');
        }}
        sx={{ bottom: snackOffset }}
      >
        <Alert
          onClose={() => {
            setToastOpen(false);
            navigate('/');
          }}
          severity="success"
          sx={{ width: '100%' }}
        >
          Drawer activated successfully!
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={errorToastOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setErrorToastOpen(false)}
        sx={{ bottom: snackOffset }}
      >
        <Alert onClose={() => setErrorToastOpen(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StartOfDay;
